import React, { forwardRef } from 'react';
import clsx from 'clsx';

/**
 * IconButton — square button containing a single icon.
 *
 * Variants: solid, soft, outline, ghost
 * Tones:    brand (default), neutral, danger
 * Sizes:    xs (28), sm (32), md (40), lg (44), xl (52)
 * Shape:    square (rounded), round (pill)
 *
 * Always pass `aria-label` or `tooltip` for accessibility.
 */
const PALETTES = {
    solid: {
        brand:   'bg-brand-500 text-ink-inverse border-brand-600/20 hover:bg-brand-600 active:bg-brand-700 shadow-sm',
        neutral: 'bg-ink text-ink-inverse border-ink hover:bg-ink-strong active:bg-ink-strong shadow-sm',
        danger:  'bg-danger-500 text-ink-inverse border-danger-600/20 hover:bg-danger-600 active:bg-danger-700 shadow-sm',
    },
    soft: {
        brand:   'bg-brand-50 text-brand-700 border-brand-100 hover:bg-brand-100 active:bg-brand-200',
        neutral: 'bg-surface-muted text-ink border-line hover:bg-surface-sunken active:bg-line',
        danger:  'bg-danger-50 text-danger-700 border-danger-100 hover:bg-danger-100 active:bg-danger-200',
    },
    outline: {
        brand:   'bg-surface text-brand-700 border-brand-300 hover:bg-brand-50 active:bg-brand-100',
        neutral: 'bg-surface text-ink border-line hover:bg-surface-muted active:bg-surface-sunken',
        danger:  'bg-surface text-danger-700 border-danger-300 hover:bg-danger-50 active:bg-danger-100',
    },
    ghost: {
        brand:   'bg-transparent text-brand-700 border-transparent hover:bg-brand-50 active:bg-brand-100',
        neutral: 'bg-transparent text-ink-muted border-transparent hover:text-ink hover:bg-surface-muted active:bg-surface-sunken',
        danger:  'bg-transparent text-danger-700 border-transparent hover:bg-danger-50 active:bg-danger-100',
    },
};

const SIZES = {
    xs: { box: 'w-7  h-7  rounded-md', icon: 'w-3.5 h-3.5' },
    sm: { box: 'w-8  h-8  rounded-md', icon: 'w-4 h-4'     },
    md: { box: 'w-10 h-10 rounded-md', icon: 'w-5 h-5'     },
    lg: { box: 'w-11 h-11 rounded-lg', icon: 'w-5 h-5'     },
    xl: { box: 'w-13 h-13 rounded-lg', icon: 'w-6 h-6'     },
};

export const IconButton = forwardRef(function IconButton({
    icon,
    variant = 'ghost',
    tone = 'neutral',
    size = 'md',
    shape = 'square',
    className,
    disabled = false,
    type = 'button',
    'aria-label': ariaLabel,
    onClick,
    ...rest
}, ref) {
    const palette = (PALETTES[variant] && (PALETTES[variant][tone] || PALETTES[variant].neutral)) || PALETTES.ghost.neutral;
    const sz = SIZES[size] || SIZES.md;

    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled}
            onClick={onClick}
            aria-label={ariaLabel}
            className={clsx(
                'inline-flex items-center justify-center border select-none shrink-0',
                'transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-smooth',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
                'active:scale-[0.96]',
                shape === 'round' ? 'rounded-pill' : sz.box,
                shape !== 'round' && sz.box.replace(/\s?rounded-\w+/, ''),
                palette,
                className,
            )}
            {...rest}
        >
            <span aria-hidden="true" className={clsx('inline-flex items-center justify-center', sz.icon)}>
                {icon}
            </span>
        </button>
    );
});

export default IconButton;
